/**
 * Pure layout math for the stage-column schedule board (Timeline "New").
 *
 * Slot times arrive as festival-wallclock "HH:MM[:SS]" strings — the API's
 * SlotResponse derives them from the stored UTC datetimes, so no timezone
 * conversion happens here. Late-night times (before noon) belong to the
 * previous *programming* day and are normalized past midnight, matching the
 * `festivalSortKey` convention used by the classic StageTimeline.
 */

/** Times before this raw minute count as "after midnight of the previous programming day". */
export const LATE_NIGHT_PIVOT_MIN = 720;

export function timeToMin(t: string): number {
  return Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
}

/** Raw wallclock minutes → programming-day minutes (00:00–11:59 → +1440). */
export function normalizeMin(t: string): number {
  const m = timeToMin(t);
  return m < LATE_NIGHT_PIVOT_MIN ? m + 1440 : m;
}

export interface TimedSlot {
  id: string;
  start_time: string | null;
  end_time: string | null;
}

export interface SlotSpan {
  id: string;
  /** Normalized programming-day minutes. */
  start: number;
  end: number;
}

/** Null when the slot has no times (TBD) — those render outside the time grid. */
export function slotSpan(s: TimedSlot): SlotSpan | null {
  if (!s.start_time || !s.end_time) return null;
  const start = normalizeMin(s.start_time);
  let end = normalizeMin(s.end_time);
  if (end <= start) end += 1440;
  return { id: s.id, start, end };
}

/** Crew shifts live LITERALLY on their chosen day — a 06:00 start is that
 *  day's morning, never the previous day's late night. The lineup's
 *  late-night pivot (normalizeMin) must not apply here: it pushed any
 *  before-08:00 start 24h right, rendering morning crew calls as if they
 *  belonged to the next day (2026-07-20 field report). Overnight shifts
 *  keep working: an end at/before the start rolls +24h. */
export function crewSlotSpan(s: TimedSlot): SlotSpan | null {
  if (!s.start_time || !s.end_time) return null;
  const start = timeToMin(s.start_time);
  let end = timeToMin(s.end_time);
  if (end <= start) end += 1440;
  return { id: s.id, start, end };
}

export interface DayAxis {
  startMin: number;
  endMin: number;
  /** Hour marks (normalized minutes), inclusive of both bounds. */
  hours: number[];
}

/** Axis covering all spans of a day, snapped outward to whole hours. */
export function computeAxis(spans: SlotSpan[]): DayAxis | null {
  if (spans.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const s of spans) {
    if (s.start < min) min = s.start;
    if (s.end > max) max = s.end;
  }
  const startMin = Math.floor(min / 60) * 60;
  const endMin = Math.ceil(max / 60) * 60;
  const hours: number[] = [];
  for (let h = startMin; h <= endMin; h += 60) hours.push(h);
  return { startMin, endMin, hours };
}

/** "HH:MM" label for a normalized minute (1530 → "01:30"). */
export function formatAxisMin(min: number): string {
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Drag snapping for the proportional grid (minutes). */
export const DRAG_SNAP_MIN = 15;

/** Convert a horizontal drag delta (px) to snapped minutes. */
export function snapDeltaMin(
  deltaPx: number,
  pxPerMin: number,
  snap: number = DRAG_SNAP_MIN,
): number {
  return Math.round(deltaPx / pxPerMin / snap) * snap;
}

export interface LaidOutSlot extends SlotSpan {
  /** Horizontal lane inside the stage column (0-based). */
  lane: number;
  /** Lane count of the overlap cluster this slot belongs to. */
  lanes: number;
}

/**
 * Assign side-by-side lanes to overlapping slots (calendar-style). Slots that
 * don't overlap anything get lane 0 of a 1-lane cluster (full width).
 */
export function layoutLanes(spans: SlotSpan[]): LaidOutSlot[] {
  const sorted = [...spans].sort((a, b) => a.start - b.start || a.end - b.end);
  const result: LaidOutSlot[] = [];
  let cluster: LaidOutSlot[] = [];
  let clusterEnd = -1;
  const laneEnds: number[] = [];

  const flush = () => {
    if (cluster.length === 0) return;
    const lanes = Math.max(...cluster.map((c) => c.lane)) + 1;
    for (const c of cluster) c.lanes = lanes;
    cluster = [];
    laneEnds.length = 0;
  };

  for (const s of sorted) {
    if (cluster.length > 0 && s.start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= s.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(s.end);
    } else {
      laneEnds[lane] = s.end;
    }
    const laid: LaidOutSlot = { ...s, lane, lanes: 1 };
    cluster.push(laid);
    result.push(laid);
    clusterEnd = Math.max(clusterEnd, s.end);
  }
  flush();
  return result;
}

/** Ids of slots that overlap another slot in the same list (same stage+day). */
export function overlappingIds(spans: SlotSpan[]): Set<string> {
  const out = new Set<string>();
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length && sorted[j]!.start < sorted[i]!.end; j++) {
      out.add(sorted[i]!.id);
      out.add(sorted[j]!.id);
    }
  }
  return out;
}

/**
 * Deterministic stage colour assignment: cycles the 6 stage identity tokens
 * by stage order. Single source for every view that tints by stage.
 */
export const STAGE_COLOR_VARS = [
  "--tr-stage-main",
  "--tr-stage-stone",
  "--tr-stage-magma",
  "--tr-stage-sunrise",
  "--tr-stage-5",
  "--tr-stage-6",
] as const;

export function stageColorVar(index: number): string {
  const n = STAGE_COLOR_VARS.length;
  return `var(${STAGE_COLOR_VARS[((index % n) + n) % n]})`;
}

/**
 * Stage tint: the admin/manager-chosen `stage.color` (hex) when set, else a
 * deterministic palette colour keyed by the stage's STABLE `palette_index`
 * (assigned once at create, never touched by reorder). Single resolver for
 * every view — custom colours propagate across all days/views, and the auto
 * palette no longer shifts when stages are reordered. The legacy positional
 * `fallbackIndex` is only used if a stage predates `palette_index` (should not
 * happen after migration 060).
 */
export function resolveStageColor(
  stage: { color?: string | null; palette_index?: number },
  fallbackIndex = 0,
): string {
  return stage.color
    ? stage.color
    : stageColorVar(stage.palette_index ?? fallbackIndex);
}

/**
 * Reconcile a locally-held stage order (list of ids) against the latest server
 * stage list. Keeps the local sequence for stages that still exist, appends
 * newly-added stages (in server order) at the end, and drops removed ones.
 *
 * It deliberately NEVER re-sorts existing stages to match the server order: in
 * the manage-stages drawer the drag is the source of truth for sequence, so a
 * refetch that merely reports a new order (e.g. the one triggered by the reorder
 * mutation we just made) must not clobber the local order — doing that mid-drag
 * is what shuffled untouched rows. Returns the SAME array reference when nothing
 * changed, so callers can bail out of a state update.
 */
export function reconcileStageOrder(
  currentIds: string[],
  stages: { id: string }[],
): string[] {
  const liveIds = stages.map((s) => s.id);
  const currentSet = new Set(currentIds);
  const liveSet = new Set(liveIds);
  const kept = currentIds.filter((id) => liveSet.has(id));
  const added = liveIds.filter((id) => !currentSet.has(id));
  if (added.length === 0 && kept.length === currentIds.length) {
    return currentIds; // unchanged membership → preserve reference
  }
  return [...kept, ...added];
}

/** The 6 preset colours (resolved hex) offered in the stage colour picker. */
export const STAGE_COLOR_PRESETS = [
  "#22d3ee",
  "#a78bfa",
  "#f0a35e",
  "#34d399",
  "#f472b6",
  "#a3e635",
] as const;

/** Bucket size of the grid "New" layout columns. */
export const BUCKET_MIN = 120;

export interface BucketRange {
  startMin: number;
  endMin: number;
  /** Start minute of each bucket, [startMin, endMin) in BUCKET_MIN steps. */
  buckets: number[];
}

/** 2-hour buckets covering all spans of a day, snapped outward. */
export function computeBuckets(spans: SlotSpan[]): BucketRange | null {
  if (spans.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const s of spans) {
    if (s.start < min) min = s.start;
    if (s.end > max) max = s.end;
  }
  const startMin = Math.floor(min / BUCKET_MIN) * BUCKET_MIN;
  const endMin = Math.ceil(max / BUCKET_MIN) * BUCKET_MIN;
  const buckets: number[] = [];
  for (let b = startMin; b < endMin; b += BUCKET_MIN) buckets.push(b);
  return { startMin, endMin, buckets };
}

/** "18–20" / "00–02" label for a bucket start minute. */
export function bucketLabel(startMin: number): string {
  return `${formatAxisMin(startMin).slice(0, 2)}–${formatAxisMin(startMin + BUCKET_MIN).slice(0, 2)}`;
}

/**
 * Map a calendar date + raw wallclock minutes to the programming day they
 * belong to (early-morning hours belong to the previous day's lineup).
 */
export function programmingDay(
  calendarDay: string,
  rawMin: number,
): { day: string; min: number } {
  if (rawMin >= LATE_NIGHT_PIVOT_MIN) return { day: calendarDay, min: rawMin };
  const [y, m, d] = calendarDay.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return { day: dt.toISOString().slice(0, 10), min: rawMin + 1440 };
}

/** Current programming day + normalized minutes in the festival timezone. */
export function nowInFestivalTz(tz: string): { day: string; min: number } | null {
  try {
    const dtf = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    const map: Record<string, string> = {};
    for (const p of dtf.formatToParts(new Date())) {
      if (p.type !== "literal") map[p.type] = p.value;
    }
    const calendarDay = `${map.year}-${map.month}-${map.day}`;
    const rawMin = Number(map.hour) * 60 + Number(map.minute);
    return programmingDay(calendarDay, rawMin);
  } catch {
    return null;
  }
}

/** "Sat, Jun 25" label from a "YYYY-MM-DD" day key — no timezone drift. */
export function formatDayLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}
